import React from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import type { Component } from '@formio/react';
import { InfoModal } from '../../../basic';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioInfoModalConstructor = {
  heading: LanguageProperty;
  modalTitle: LanguageProperty;
  modalContent: LanguageProperty;
  modalList: string | null;
  orderedList: boolean;
  ariaLabel: LanguageProperty;
};

/**
 *
 * @param heading The title that is shown beside the Info Icon.
 * @param modalTitle The (optional) title that is shown when the modal opens.
 * @param modalContent The (optional) content that is shown inside the modal.
 * @param modalList The (optional) list that is shown underneath the content inside the modal.
 * @param orderedList Defines whether the list should be <ol> or <ul>.
 */
export type EhsFormioInfoModalSettings = {
  heading: LanguageProperty;
  modalTitle?: LanguageProperty;
  modalContent?: LanguageProperty | React.JSX.Element;
  modalList?: Array<LanguageProperty>;
  orderedList: boolean;
  ariaLabel: LanguageProperty;
};
export const EhsFormioInfoModalType = 'ehsinfomodal';
export class EhsFormioInfoModal extends EhsFormioReactComponent<
  EhsFormioInfoModalSettings,
  EhsFormioInfoModalConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioInfoModalType,
      key: EhsFormioInfoModalType,
      heading: 'common.unknown',
      modalTitle: 'common.unknown',
      modalContent: 'common.unknown',
      modalList: 'common.unknown\ncommon.unknown',
      orderedList: false,
      ariaLabel: 'common.unknown',
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-InfoModal',
      group: 'advanced',
      icon: 'file',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioInfoModal.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'heading',
          label: 'Heading',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'modalTitle',
          label: 'Title of the Modal',
          tooltip: 'Please add only valid translation keys (mandatory field)',
          weight: 20
        },
        {
          type: 'textfield',
          key: 'modalContent',
          label: 'Content of the Modal',
          tooltip: 'Please add only valid translation keys',
          weight: 30
        },
        {
          type: 'textarea',
          key: 'modalList',
          label: 'Optional List (shown below Content)',
          tooltip:
            'Please add only valid translation keys, each list item in a new line (separated by newline)',
          weight: 40
        },
        {
          type: 'checkbox',
          key: 'orderedList',
          label: 'Show as ordered list',
          tooltip:
            'Set to true if you want to see the list items as ordered list(1.2.3...), set to false for an unordered list (bullets)',
          weight: 50
        }
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private heading: LanguageProperty = 'common.unknown';
  private modalTitle: LanguageProperty = 'common.unknown';
  private modalContent: LanguageProperty = 'common.unknown';
  private modalList: Array<LanguageProperty> = [
    'common.unknown',
    'common.unknown'
  ];
  private orderedList = false;
  private ariaLabel: LanguageProperty = 'common.unknown';

  extractFields(component: Component<EhsFormioInfoModalConstructor>): void {
    this.heading = component.heading;
    this.modalTitle = component.modalTitle;
    this.modalContent = component.modalContent;
    // modalList:
    // a workaround to show a list of language keys we get from the form builder as string separated by new lines
    const array = (component.modalList ?? '').split('\n');
    this.modalList = array.filter(
      value => value !== ''
    ) as Array<LanguageProperty>;
    this.orderedList = component.orderedList;
    this.ariaLabel = component.ariaLabel;
  }

  attachEhsReact(): [
    (
      props: EhsFormioReactProps<EhsFormioInfoModalSettings>
    ) => React.JSX.Element,
    EhsFormioInfoModalSettings
  ] {
    return [
      InfoModalCmp,
      {
        heading: this.heading,
        modalTitle: this.modalTitle,
        modalContent: this.modalContent,
        modalList: this.modalList,
        orderedList: this.orderedList,
        ariaLabel: this.ariaLabel
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

export type InfoModalCmpProps = Omit<
  EhsFormioReactProps<EhsFormioInfoModalSettings>,
  | 'value'
  | 'onChange'
  | 'label'
  | 'readOnly'
  | 'required'
  | 'keyForComponent'
  | 'getErrors'
>;

export function InfoModalCmp({
  settings
}: InfoModalCmpProps): React.JSX.Element {
  return <InfoModal settings={settings} />;
}
